import React from 'react';
import { useMsal } from '@azure/msal-react';
import {
  Section,
  Eyebrow,
  Headline,
  Lead,
  Card,
  Grid,
  Col,
} from '../components/iss/IssPrimitives';
import AppFooter from '../components/AppFooter';
import './HomePage.scss';

const INTENTS = [
  'Test a new or emerging technology,',
  'Evaluate a vendor solution or technical concept,',
  'Validate business value or user adoption before scaling,',
  'Gather data to support a go / no-go decision for rollout.',
];

type PreviewPhase = {
  name: string;
  detail: string;
  state: 'done' | 'current' | 'upcoming';
};

const PREVIEW_PHASES: PreviewPhase[] = [
  { name: 'Initiation', detail: 'Scorecard and business justification', state: 'done' },
  { name: 'Preparation', detail: 'NDA, IT security, legal, procurement', state: 'current' },
  { name: 'Running', detail: 'Set duration and reminders', state: 'upcoming' },
  { name: 'Evaluation', detail: 'Success criteria and outcome', state: 'upcoming' },
];

const PREVIEW_CHIPS = ['NDA', 'Data protection', 'IT security', 'Legal'];

/** Derive a short greeting name from MSAL name or UPN/email. */
function greetingNameFrom(displayName: string): string {
  const trimmed = displayName.trim();

  // "Last, First Middle" (common Entra displayName)
  if (trimmed.includes(',')) {
    const given = trimmed.split(',')[1]?.trim() ?? '';
    return given.split(/\s+/).filter(Boolean)[0] || trimmed;
  }

  // UPN / email — use local-part only (never the full address)
  if (trimmed.includes('@')) {
    const local = trimmed.split('@')[0] || trimmed;
    return local.split(/[._\s-]+/).filter(Boolean)[0] || local;
  }

  return trimmed.split(/\s+/).filter(Boolean)[0] || trimmed;
}

const HomePage: React.FC = () => {
  const { accounts } = useMsal();
  const account = accounts[0];

  const displayName = account?.name || account?.username || 'there';
  const greetingName = greetingNameFrom(displayName);

  return (
    <div className="home-page">
      <Section theme="dark" spacing="tight">
        <Eyebrow>Proof of Concept Platform</Eyebrow>
        <Headline as="h1" size="4xl">
          Welcome back, {greetingName}
        </Headline>
        <Lead>
          Your one-stop platform for managing proofs of concept.
        </Lead>
      </Section>

      <Section theme="dark" className="home-actions">
        <Eyebrow>Get started</Eyebrow>
        <Headline as="h2" size="3xl">
          Choose an action
        </Headline>
        <Grid>
          <Col span={4} md={4} lg={3}>
            <Card
              to="/pocs/new"
              meta="Action"
              title="Start a New PoC"
              text="Start a new proof of concept. Please make yourself familiar with PoC guidelines before initiating."
            />
          </Col>
          <Col span={4} md={4} lg={3}>
            <Card
              to="/my-pocs"
              meta="Library"
              title="My PoCs"
              text="View my own submitted PoCs"
            />
          </Col>
          <Col span={4} md={4} lg={3}>
            <Card
              to="/all-pocs"
              meta="Library"
              title="All PoCs"
              text="View all submitted PoCs"
            />
          </Col>
          <Col span={4} md={4} lg={3}>
            <Card
              to="/guidebook"
              meta="Guidance"
              title="PoC Guidelines"
              text="Everything you need to know before starting a Proof of Concept with an external supplier."
            />
          </Col>
        </Grid>
      </Section>

      <Section theme="light">
        <div className="home-about">
          <div className="home-about__copy">
            <Eyebrow>About</Eyebrow>
            <Headline as="h2" size="3xl">
              Your One-Stop Platform for Managing Proofs of Concept
            </Headline>
            <Lead>What is a Proof of Concept (POC)?</Lead>
            <p className="home-about__text">
              A Proof of Concept (POC) is a structured, time-bound initiative that
              aims to verify the feasibility, functionality, or value of a specific
              technology, solution, or approach before making a full-scale
              investment. The purpose of a POC is to provide evidence-based
              validation — confirming whether a proposed innovation works as
              intended, integrates with existing systems, and delivers measurable
              business benefits.
            </p>
            <p className="home-about__text home-about__text--strong">
              Submit your POC to this platform if you intend to:
            </p>
            <ul className="home-about__checks">
              {INTENTS.map((intent) => (
                <li key={intent}>
                  <span className="home-about__check" aria-hidden />
                  {intent}
                </li>
              ))}
            </ul>
            <p className="home-about__text">
              The platform will guide you through all key steps — from submission
              and approval, through execution, to evaluation and closure — ensuring
              consistency, transparency, and alignment with company standards.
            </p>
          </div>

          <aside className="home-preview" aria-label="Example of a PoC journey">
            <div className="home-preview__card">
              <span className="home-preview__badge">Example</span>
              <p className="home-preview__eyebrow">PoC journey</p>
              <h3 className="home-preview__title">Smart washroom sensors</h3>

              <ol className="home-preview__phases">
                {PREVIEW_PHASES.map((phase, index) => (
                  <li
                    key={phase.name}
                    className={`home-preview__phase home-preview__phase--${phase.state}`}
                  >
                    <span className="home-preview__dot">{index + 1}</span>
                    <div className="home-preview__phase-body">
                      <strong>{phase.name}</strong>
                      <span>{phase.detail}</span>
                      {phase.state === 'current' ? (
                        <div className="home-preview__detail">
                          <div className="home-preview__chips">
                            {PREVIEW_CHIPS.map((chip) => (
                              <span key={chip} className="home-preview__chip">
                                {chip}
                              </span>
                            ))}
                          </div>
                          <span className="home-preview__bar" />
                          <span className="home-preview__bar home-preview__bar--short" />
                        </div>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ol>

              <div className="home-preview__footer">
                <span className="home-preview__score">Score 46 · Great!</span>
                <span className="home-preview__meta">4 phases · guided steps</span>
              </div>
            </div>
          </aside>
        </div>
      </Section>
      <AppFooter />
    </div>
  );
};

export default HomePage;
